/**
 * Tab behavior for the Dose Ingredient Explorer section (WAI-ARIA Tabs
 * pattern). Adapted from assets/dose-clinical-studies.js.
 *
 * Products are theme blocks, so the section can't wrap them in
 * server-rendered tab markup — this element builds the product (SKU) tab bar
 * from each panel's data-tab-label when two or more products exist. Since
 * ingredients are themselves nested theme blocks (dynamic count, no fixed
 * cap), the per-product ingredient nav is built the same way, from each
 * ingredient panel's data-name and thumbnail template. Both tab layers share
 * the same delegated activation and keyboard handling, since it's generic
 * over any [role="tab"]/[role="tablist"]/[role="tabpanel"] triple.
 */
class DoseIngredientExplorer extends HTMLElement {
  connectedCallback() {
    this.setupSkuTabs();
    this.setupIngredientTabs();
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  setupSkuTabs() {
    
    const panels = [...this.querySelectorAll('[data-product-panel]')];
    if (panels.length < 2 || this.querySelector('.dose-ingredient-explorer__sku-tabs')) return;

    const sectionId = this.dataset.sectionId || 'dose-ingredient-explorer';
    const tablist = document.createElement('div');
    tablist.className = 'dose-ingredient-explorer__sku-tabs';
    tablist.setAttribute('role', 'tablist');
    tablist.setAttribute('aria-label', 'Products');
    tablist.style.setProperty('--dose-ingredient-explorer-tab-count', panels.length);
    tablist.style.setProperty('--dose-ingredient-explorer-active-index', 0);

    panels.forEach((panel, index) => {
      const panelId = `${sectionId}-product-${index + 1}-panel`;
      const tabId = `${sectionId}-product-${index + 1}-tab`;
      const selected = index === 0;

      panel.id = panelId;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.hidden = !selected;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'dose-ingredient-explorer__sku-tab';
      tab.id = tabId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      tab.textContent = this.formatTabLabel(panel.dataset.tabLabel || `Product ${index + 1}`);
      tablist.append(tab);
    });

    const indicator = document.createElement('span');
    indicator.className = 'dose-ingredient-explorer__sku-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    tablist.prepend(indicator);

    this.prepend(tablist);
    this.classList.add('dose-ingredient-explorer--tabbed');
  }

  /**
   * Builds each product's ingredient nav tab bar from its hero panels'
   * data-name and thumbnail <template>, since ingredients are nested blocks
   * (as few or as many as a merchant adds) and can't be pre-wrapped in tab
   * markup the way a fixed set of fields could be.
   */
  setupIngredientTabs() {
    let groupIndex = 0;

    for (const navColumn of this.querySelectorAll('[data-ingredient-nav-column]')) {
      const hero = navColumn.parentElement?.querySelector('[data-ingredient-hero]');
      const panels = hero ? [...hero.querySelectorAll('[data-ingredient-panel]')] : [];
      if (!panels.length || navColumn.querySelector('[role="tablist"]')) continue;

      groupIndex += 1;
      const idPrefix = `${this.dataset.sectionId || 'dose-ingredient-explorer'}-ingredients-${groupIndex}`;

      const tablist = document.createElement('div');
      tablist.className = 'dose-ingredient-explorer__nav';
      tablist.setAttribute('role', 'tablist');
      tablist.setAttribute('aria-label', 'Ingredients');

      panels.forEach((panel, index) => {
        const panelId = `${idPrefix}-${index + 1}-panel`;
        const tabId = `${idPrefix}-${index + 1}-tab`;
        const selected = index === 0;

        panel.id = panelId;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabId);
        panel.hidden = !selected;

        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'dose-ingredient-explorer__nav-tab';
        tab.id = tabId;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panelId);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');

        const thumb = document.createElement('span');
        thumb.className = 'dose-ingredient-explorer__nav-thumb';
        const template = panel.querySelector('template[data-ingredient-thumb]');
        if (template) thumb.append(template.content.cloneNode(true));
        tab.append(thumb);

        const name = document.createElement('span');
        name.className = 'dose-ingredient-explorer__nav-name';
        name.textContent = panel.dataset.name || '';
        tab.append(name);

        tablist.append(tab);

        /* Below makes ingredient hero number labels match the tab order;
         * Can't be done in CSS because the CSS counter is dependent upon all numbered items being visible.
         */
        const tab_label = panel.querySelector('.dose-ingredient-explorer__badge');
        if(tab_label) {
          tab_label.innerHTML = `${index + 1}`;
        }
      });

      navColumn.prepend(tablist);
    }
  }

  /**
   * Glue everything after the first two words with non-breaking spaces so a
   * wrapping label breaks after the lead-in and keeps the product name on one
   * line ("DOSE FOR / YOUR LIVER", never "DOSE FOR YOUR / LIVER").
   */
  formatTabLabel(label) {
    const words = label.split(' ');
    if (words.length <= 2) return label;
    return `${words[0]} ${words[1]} ${words.slice(2).join(' ')}`;
  }

  handleClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;
    this.activateTab(tab);
  };

  handleKeydown = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;

    const tablist = tab.closest('[role="tablist"]');
    if (!tablist) return;

    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    const index = tabs.indexOf(tab);
    let next = null;

    if (event.key === 'ArrowRight') {
      next = tabs[(index + 1) % tabs.length];
    } else if (event.key === 'ArrowLeft') {
      next = tabs[(index - 1 + tabs.length) % tabs.length];
    } else if (event.key === 'Home') {
      next = tabs[0];
    } else if (event.key === 'End') {
      next = tabs[tabs.length - 1];
    }

    if (!next) return;

    event.preventDefault();
    next.focus();
    this.activateTab(next);
  };

  handleBlockSelect = (event) => {
    let panel = event.target.closest?.('[role="tabpanel"]');

    while (panel) {
      const tab = this.querySelector(`[role="tab"][aria-controls="${panel.id}"]`);
      if (tab) this.activateTab(tab);
      panel = panel.parentElement ? panel.parentElement.closest('[role="tabpanel"]') : null;
    }
  };

  activateTab(tab) {
    const tablist = tab.closest('[role="tablist"]');
    if (!tablist) return;

    [...tablist.querySelectorAll('[role="tab"]')].forEach((other, index) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', selected ? 'true' : 'false');
      other.setAttribute('tabindex', selected ? '0' : '-1');

      const panel = document.getElementById(other.getAttribute('aria-controls'));

      if (panel) {
        panel.hidden = !selected;
      }

      if (selected) {
        tablist.style.setProperty('--dose-ingredient-explorer-active-index', index);
      }
    });
  }
}

if (!customElements.get('dose-ingredient-explorer')) {
  customElements.define('dose-ingredient-explorer', DoseIngredientExplorer);
}
